// Chart of Accounts · Account drill-in view (shown on /accounting/chart-of-accounts/:accountId).
// Four inline stats (Option B labels), filter bar, transactions table (plain text columns, no colored pills),
// right-side details + receipts panel. Owns the local filter + search state.

import React, { useMemo, useState } from "react";
import { Download, FileText, Search } from "lucide-react";
import type { Account, ReceiptFile, Transaction } from "../types";
import { PALETTE } from "../constants";
import { fmtBal, fmtCurrency } from "../utils";

type FilterKey = "thisMonth" | "last90" | "ytd" | "custom";

interface DrillViewProps {
  account: Account;
  transactions: Transaction[];
  receipts: ReceiptFile[];
}

export default function DrillView({ account, transactions, receipts }: DrillViewProps) {
  const [filter, setFilter] = useState<FilterKey>("thisMonth");
  const [search, setSearch] = useState<string>("");

  const filteredTx = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((t) =>
      t.description.toLowerCase().includes(q) ||
      t.contact.toLowerCase().includes(q) ||
      (t.reference || "").toLowerCase().includes(q),
    );
  }, [transactions, search]);

  const missingReceipts = (account.receiptsTotal || 0) - (account.receiptsPresent || 0);

  return (
    <div style={styles.grid}>
      <div>
        {/* Inline 4-stat strip — Option B labels */}
        <div style={styles.statsRow}>
          <StatItem
            label="Balance (YTD)"
            value={fmtBal(account.balanceYTD || 0)}
            meta="↑ 12% vs last year"
            color={account.type === "Income" ? PALETTE.tealDark : PALETTE.ink}
          />
          <StatItem
            label="This month"
            value={fmtBal(account.balanceThisMonth || 0)}
            meta={`${transactions.length} transactions`}
          />
          <StatItem
            label="Receipts"
            value={`${account.receiptsPresent || 0} of ${account.receiptsTotal || 0}`}
            meta={missingReceipts > 0 ? `${missingReceipts} missing, Vela flagged` : "All matched"}
          />
          <StatItem
            label="Last activity"
            value={account.lastActivityDate || "n/a"}
            meta={account.lastActivityDescription || ""}
            smallValue
          />
        </div>

        {/* Filter bar */}
        <div style={styles.filterBar}>
          <FilterChip active={filter === "thisMonth"} onClick={() => setFilter("thisMonth")}>This month</FilterChip>
          <FilterChip active={filter === "last90"} onClick={() => setFilter("last90")}>Last 90 days</FilterChip>
          <FilterChip active={filter === "ytd"} onClick={() => setFilter("ytd")}>YTD</FilterChip>
          <FilterChip active={filter === "custom"} onClick={() => setFilter("custom")}>Custom</FilterChip>
          <div style={styles.searchBox}>
            <Search size={14} color={PALETTE.ink4} />
            <input
              style={styles.searchInput}
              placeholder="Search transactions, contacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Transactions table — plain text columns, no colored pills */}
        <div style={styles.txHead}>
          <div>Date</div>
          <div>Description</div>
          <div>Method</div>
          <div>Contact</div>
          <div style={{ textAlign: "right" }}>Amount</div>
          <div style={{ textAlign: "center" }}>Receipt</div>
          <div style={{ textAlign: "right" }}>Balance</div>
        </div>
        {filteredTx.length === 0 ? (
          <div style={styles.emptyRow}>No transactions match your search.</div>
        ) : (
          filteredTx.map((t) => (
            <div key={t.id} style={styles.txRow}>
              <div style={styles.txDate}>{t.date}</div>
              <div>
                <div style={styles.txDesc}>{t.description}</div>
                {(t.reference || t.billingType) && (
                  <div style={styles.txDescSub}>
                    {t.reference}
                    {t.billingType && (
                      <>
                        <span style={styles.subSep}>·</span>
                        <span>{t.billingType}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div style={styles.methodPlain}>{t.method}</div>
              <div style={styles.txContact}>{t.contact}</div>
              <div style={{ ...styles.txAmt, color: t.amount >= 0 ? PALETTE.tealDark : PALETTE.ink }}>
                {fmtCurrency(t.amount)}
              </div>
              <div style={{ textAlign: "center" }}>
                {t.hasReceipt ? (
                  <span style={styles.rcLink}>View</span>
                ) : (
                  <span style={styles.rcNone}>None</span>
                )}
              </div>
              <div style={styles.txBal}>{fmtBal(t.runningBalance)}</div>
            </div>
          ))
        )}

        <div style={styles.drillFoot}>
          <div>Showing {filteredTx.length} of {transactions.length} transactions</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={styles.btnSm}>Load more</button>
          </div>
        </div>
      </div>

      {/* Right-side panel */}
      <aside style={styles.sidePanel} aria-label="Account details">
        <div style={styles.sideHead}>Account details</div>
        <SideItem label="Number" value={account.number} />
        <SideItem label="Type" value={account.type} />
        {account.detailType && <SideItem label="Detail type" value={account.detailType} />}
        {account.taxTreatment && <SideItem label="GST/HST" value={account.taxTreatment} />}
        {account.openedDate && <SideItem label="Opened" value={account.openedDate} />}

        <div style={{ ...styles.sideHead, marginTop: 28 }}>
          Recent receipts ({account.receiptsPresent || 0})
        </div>
        {receipts.map((r) => (
          <div key={r.id} style={styles.receiptRow}>
            <div style={styles.receiptIcon}><FileText size={14} /></div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={styles.receiptName}>{r.filename}</div>
              <div style={styles.receiptMeta}>{r.date} · {r.size}</div>
            </div>
            <div style={styles.receiptOpen}>Open</div>
          </div>
        ))}
        {(account.receiptsPresent || 0) > receipts.length && (
          <button style={styles.downloadAllBtn}>
            <Download size={13} /> Download all {account.receiptsPresent} receipts
          </button>
        )}
      </aside>
    </div>
  );
}

// === Local sub-components ===

function StatItem({ label, value, meta, color, smallValue }: {
  label: string; value: string; meta: string; color?: string; smallValue?: boolean;
}) {
  return (
    <div>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: color || PALETTE.ink, fontSize: smallValue ? 16 : 20 }}>
        {value}
      </div>
      {meta && <div style={styles.statMeta}>{meta}</div>}
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <span style={{ ...styles.filterChip, ...(active ? styles.filterChipActive : {}) }} onClick={onClick}>
      {children}
    </span>
  );
}

function SideItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.sideItem}>
      <span style={styles.sideItemLabel}>{label}</span>
      <span style={styles.sideItemValue}>{value}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  grid: { display: "grid", gridTemplateColumns: "1fr 320px", gap: 32 },

  // Stats row — Option B
  statsRow: {
    display: "grid", gridTemplateColumns: "repeat(4, auto)", gap: 32,
    paddingBottom: 24, borderBottom: `1px solid ${PALETTE.line}`, marginBottom: 24,
    alignItems: "baseline",
  },
  statLabel: {
    fontSize: 12.5, fontWeight: 500, color: PALETTE.ink3, marginBottom: 6,
  },
  statValue: {
    fontSize: 20, fontWeight: 800, color: PALETTE.ink,
    fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em",
  },
  statMeta: { fontSize: 11.5, color: PALETTE.ink4, fontWeight: 500, marginTop: 2 },

  // Filter bar
  filterBar: {
    display: "flex", gap: 10, alignItems: "center",
    marginBottom: 20, paddingBottom: 14, borderBottom: `1px solid ${PALETTE.line}`,
  },
  filterChip: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "6px 12px", border: `1px solid ${PALETTE.line}`, borderRadius: 999,
    fontSize: 12, fontWeight: 700, color: PALETTE.ink, background: "#fff", cursor: "pointer",
  },
  filterChipActive: { background: PALETTE.ink, color: "#fff", borderColor: PALETTE.ink },
  searchBox: {
    marginLeft: "auto", display: "flex", alignItems: "center", gap: 8,
    background: PALETTE.panel, border: `1px solid ${PALETTE.line}`, borderRadius: 8,
    padding: "6px 12px", width: 260,
  },
  searchInput: {
    border: 0, background: "transparent", fontSize: 12.5, width: "100%",
    outline: "none", fontFamily: "inherit", color: PALETTE.ink,
  },

  // Transactions table — restrained, plain text columns
  txHead: {
    display: "grid",
    gridTemplateColumns: "90px 1fr 110px 110px 110px 70px 90px",
    gap: 16, padding: "10px 0",
    fontSize: 11, fontWeight: 500, color: PALETTE.ink3,
    borderBottom: `1px solid ${PALETTE.line}`,
  },
  txRow: {
    display: "grid",
    gridTemplateColumns: "90px 1fr 110px 110px 110px 70px 90px",
    gap: 16, padding: "14px 0", borderBottom: `1px solid ${PALETTE.lineSoft}`,
    alignItems: "center",
  },
  emptyRow: { padding: "40px 0", textAlign: "center", color: PALETTE.ink4, fontSize: 13, fontWeight: 500 },
  txDate: { fontSize: 12.5, color: PALETTE.ink2, fontWeight: 600 },
  txDesc: { fontSize: 13, fontWeight: 700, color: PALETTE.ink },
  txDescSub: {
    fontSize: 11.5, color: PALETTE.ink4, marginTop: 2, fontWeight: 500,
    display: "flex", alignItems: "center", flexWrap: "wrap",
  },
  subSep: { color: PALETTE.line, margin: "0 6px" },
  methodPlain: { fontSize: 12, color: PALETTE.ink3, fontWeight: 500 },
  txContact: { fontSize: 12.5, fontWeight: 600, color: PALETTE.ink2 },
  txAmt: { textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums", fontSize: 13.5 },
  rcLink: { fontSize: 12, color: PALETTE.tealDark, fontWeight: 700, cursor: "pointer" },
  rcNone: { fontSize: 11.5, color: PALETTE.ink4, fontWeight: 500 },
  txBal: { textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: 13, color: PALETTE.ink },

  drillFoot: {
    marginTop: 20, paddingTop: 16, borderTop: `1px solid ${PALETTE.line}`,
    display: "flex", justifyContent: "space-between", alignItems: "center",
    fontSize: 12.5, color: PALETTE.ink4, fontWeight: 500,
  },
  btnSm: {
    display: "inline-flex", alignItems: "center", gap: 7, height: 30,
    padding: "0 12px", borderRadius: 7, fontSize: 12, fontWeight: 700,
    border: `1px solid ${PALETTE.line}`, background: "#fff", color: PALETTE.ink,
    cursor: "pointer", fontFamily: "inherit",
  },

  // Side panel
  sidePanel: { paddingLeft: 24, borderLeft: `1px solid ${PALETTE.lineSoft}` },
  sideHead: {
    fontSize: 12.5, fontWeight: 500, color: PALETTE.ink3, marginBottom: 14,
  },
  sideItem: {
    padding: "10px 0", borderBottom: `1px solid ${PALETTE.lineSoft}`,
    display: "flex", justifyContent: "space-between",
    fontSize: 12.5, fontWeight: 500, color: PALETTE.ink2,
  },
  sideItemLabel: { color: PALETTE.ink4 },
  sideItemValue: { color: PALETTE.ink, fontWeight: 700 },

  receiptRow: {
    display: "grid", gridTemplateColumns: "32px 1fr auto", gap: 10,
    padding: "8px 0", borderBottom: `1px solid ${PALETTE.lineSoft}`,
    alignItems: "center", cursor: "pointer",
  },
  receiptIcon: {
    width: 28, height: 28, borderRadius: 6,
    background: PALETTE.panel, color: PALETTE.ink3,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  receiptName: { fontSize: 12, fontWeight: 700, color: PALETTE.ink },
  receiptMeta: { fontSize: 10.5, color: PALETTE.ink4, marginTop: 1, fontWeight: 500 },
  receiptOpen: { fontSize: 11, color: PALETTE.tealDark, fontWeight: 700, cursor: "pointer" },

  downloadAllBtn: {
    display: "inline-flex", alignItems: "center", gap: 7,
    height: 30, padding: "0 12px", borderRadius: 7, fontSize: 12, fontWeight: 700,
    border: `1px solid ${PALETTE.line}`, background: "#fff", color: PALETTE.ink,
    cursor: "pointer", fontFamily: "inherit",
    marginTop: 14, width: "100%", justifyContent: "center",
  },
};